import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
const PILLARS_DATA = [
  {
    id: "01",
    title: "CONTROL",
    heading: "PRECISION MANUFACTURING SINCE 1984",
    description:
      "Since 1984, FOKAL has delivered precision-engineered automotive and defence lighting solutions. Our manufacturing approach emphasizes process discipline, certified quality systems, and complete batch traceability across two ISO/IATF certified facilities in Faridabad and Palwal, supported by over 20 automated production lines.",
  },
  {
    id: "02",
    title: "MANAGEMENT",
    heading: "CERTIFIED QUALITY & PROCESS DISCIPLINE",
    description:
      "FOKAL operates under globally recognised quality management systems including IATF 16949:2016 and ISO 9001:2015. These frameworks govern production planning, inspection, documentation, and traceability, enabling scalable operations and consistent delivery for high-volume OEM and institutional programs.",
  },
  {
    id: "03",
    title: "PROTECT",
    heading: "DEFENCE & REGULATORY COMPLIANCE",
    description:
      "FOKAL is an approved supplier to India’s defence and institutional sectors, including the Indian Army, Indian Navy, Indian Air Force, Hindustan Aeronautics Limited (HAL), Ordnance Factory Board, Indian Railways, and the Ministry of Defence. Our products meet international and Indian standards such as ECE R37, AIS, and DOT, with approvals from DGQA and CEMILAC.",
  },
  {
    id: "04",
    title: "INNOVATION",
    heading: "IN-HOUSE ENGINEERING & TESTING",
    description:
      "Our in-house engineering capabilities support end-to-end product development and validation. Facilities include optical engineering and CAD/CAE, rapid prototyping, platform-specific customisation, photometric engineering, vision-based inspection systems, and dedicated life-testing bays supporting endurance testing beyond 1,000 hours.",
  },
  {
    id: "05",
    title: "STRATEGY",
    heading: "LONG-TERM OEM & INSTITUTIONAL PARTNERSHIPS",
    description:
      "As a trusted supplier to leading automotive OEMs and India’s Armed Forces, FOKAL focuses on long-term partnerships built on reliability, compliance, and engineering-led collaboration. Our strategy prioritises sustained engagement over transactional supply, aligning manufacturing and development with evolving mobility and defence requirements.",
  },
];

const PillarsSection = ({ isDesktop, THEME }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isInSection, setIsInSection] = useState(false);
  const sectionRef = useRef(null);
  const accumulatedScrollRef = useRef(0);
  const hasStartedHijackRef = useRef(false);
  const touchStartYRef = useRef(0);

  // Scroll hijacking logic - Desktop and Mobile
  useEffect(() => {
    const handleWheel = (e) => {
      // Hijack on both desktop and mobile
      if (!isInSection && !hasStartedHijackRef.current) return;

      const pillarHeight = 80; // Adjust based on your design
      const scrollNeeded = pillarHeight * 2; // Amount of scroll needed per pillar
      const maxScroll = scrollNeeded * (PILLARS_DATA.length - 1);

      // Mark that hijacking has started
      if (isInSection) {
        hasStartedHijackRef.current = true;
      }

      // Release hijack at end (scrolling down) or start (scrolling up from bottom)
      const isAtEnd = accumulatedScrollRef.current >= maxScroll && e.deltaY > 0;
      const isAtStart = accumulatedScrollRef.current <= 0 && e.deltaY < 0;

      if (isAtEnd || isAtStart) {
        hasStartedHijackRef.current = false;
        return; // Allow normal scroll
      }

      e.preventDefault();
      accumulatedScrollRef.current += e.deltaY;

      // Clamp accumulated scroll between 0 and max
      accumulatedScrollRef.current = Math.max(
        0,
        Math.min(accumulatedScrollRef.current, maxScroll),
      );

      // Calculate which pillar we're on
      const newIndex = Math.min(
        Math.floor(accumulatedScrollRef.current / scrollNeeded),
        PILLARS_DATA.length - 1,
      );
      const progress =
        (accumulatedScrollRef.current % scrollNeeded) / scrollNeeded;

      setActiveIndex(newIndex);
      setScrollProgress(progress);
    };

    const handleTouchStart = (e) => {
      if (isDesktop || !isInSection) return;
      touchStartYRef.current = e.touches[0].clientY;
      hasStartedHijackRef.current = true;
    };

    const handleTouchMove = (e) => {
      if (isDesktop || (!isInSection && !hasStartedHijackRef.current)) return;

      const currentY = e.touches[0].clientY;
      const deltaY = touchStartYRef.current - currentY; // Inverted because touch Y increases downward

      if (Math.abs(deltaY) < 2) return; // Ignore small movements

      const pillarHeight = 50; // Reduced for easier reach on mobile
      const scrollNeeded = pillarHeight * 1; // Less scroll needed per pillar
      const maxScroll = scrollNeeded * (PILLARS_DATA.length - 1);

      // Always prevent default on mobile during hijack
      e.preventDefault();
      accumulatedScrollRef.current += deltaY;
      touchStartYRef.current = currentY; // Update for next movement

      // Clamp accumulated scroll between 0 and max
      accumulatedScrollRef.current = Math.max(
        0,
        Math.min(accumulatedScrollRef.current, maxScroll),
      );

      // Calculate which pillar we're on
      const newIndex = Math.min(
        Math.floor(accumulatedScrollRef.current / scrollNeeded),
        PILLARS_DATA.length - 1,
      );
      const progress =
        (accumulatedScrollRef.current % scrollNeeded) / scrollNeeded;

      setActiveIndex(newIndex);
      setScrollProgress(progress);
    };

    const handleTouchEnd = () => {
      if (isDesktop) return;
      touchStartYRef.current = 0;
      // Don't reset hasStartedHijackRef here - let it persist until section exit
    };

    // Intersection Observer to detect when section is in view
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          setIsInSection(true);
        } else if (!entry.isIntersecting) {
          setIsInSection(false);
          accumulatedScrollRef.current = 0;
          hasStartedHijackRef.current = false; // Reset hijack when leaving section
        }
      },
      { threshold: [0.5, 0.9] },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    if (isInSection || hasStartedHijackRef.current) {
      window.addEventListener("wheel", handleWheel, { passive: false });
      window.addEventListener("touchstart", handleTouchStart, {
        passive: true,
      });
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleTouchEnd, { passive: true });
    }

    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
      observer.disconnect();
    };
  }, [isInSection, isDesktop]);

  return (
    <section
      id="about"
      ref={sectionRef}
      style={{
        backgroundColor: "transparent", // Creating contrast with the dark hero
        color: "#09090b",
        // Responsive padding: less on mobile to save space
        padding: isDesktop ? "120px 0" : "60px 24px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          paddingLeft: isDesktop ? "100px" : "0", // Aligning with the sidebar width + padding
          paddingRight: isDesktop ? "48px" : "0",
          display: "flex",
          flexDirection: isDesktop ? "row" : "column",
          gap: isDesktop ? "64px" : "24px", // Reduced gap for mobile list
          alignItems: "flex-start",
        }}
      >
        {/* LEFT COLUMN: THE PILLARS LIST */}
        <div
          style={{
            flex: "1",
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: isDesktop ? "16px" : "24px",
          }}
        >
          {PILLARS_DATA.map((item, index) => {
            const isActive = index === activeIndex;
            const isNext = index === activeIndex + 1;
            const showProgress = isNext ? scrollProgress : isActive ? 1 : 0;

            return (
              <div
                key={item.id}
                style={{
                  cursor: "default",
                  position: "relative",
                  display: "flex",
                  alignItems: isDesktop ? "baseline" : "flex-start",
                  gap: "16px",
                  // Remove blue tap highlight on mobile
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                {/* Mobile View: Render Title + Content Stacked */}
                {!isDesktop ? (
                  <div style={{ width: "100%" }}>
                    <h2
                      style={{
                        // Fluid typography: scales between 32px and 48px based on screen width
                        fontSize: "clamp(32px, 10vw, 48px)",
                        fontWeight: 900,
                        textTransform: "uppercase",
                        margin: "0 0 12px 0",
                        lineHeight: 0.9,
                        color: isActive ? THEME.colors.primary : "transparent", // Active text is Black
                        WebkitTextStroke: isActive ? "none" : "1px #d4d4d8", // Zinc 300 stroke
                        opacity: isActive ? 1 : Math.min(showProgress + 0.3, 1),
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "flex-start",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.5em",
                          marginRight: "12px",
                          marginTop: "6px", // Optical alignment
                          color: isActive ? THEME.colors.accent : "transparent", // Gold Number
                          WebkitTextStroke: isActive ? "none" : "1px #d4d4d8",
                        }}
                      >
                        {item.id}.
                      </span>
                      {item.title}
                    </h2>

                    {/* Scroll-triggered expand/collapse for Mobile */}
                    <AnimatePresence>
                      {isActive && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: "easeInOut" }}
                          style={{ overflow: "hidden" }}
                        >
                          {/* Indent content to align with text, not number */}
                          <div style={{ paddingLeft: "calc(0.5em + 12px)" }}>
                            <h3
                              style={{
                                fontSize: "16px",
                                fontWeight: 800,
                                color: THEME.colors.accent, // Gold Heading
                                textTransform: "uppercase",
                                marginBottom: "8px",
                                marginTop: "0px",
                                letterSpacing: "0.05em",
                              }}
                            >
                              {item.heading}
                            </h3>
                            <p
                              style={{
                                fontSize: "14px",
                                color: "#52525b",
                                lineHeight: 1.6,
                                marginBottom: "24px",
                              }}
                            >
                              {item.description}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  // Desktop View: Just the Giant Titles with scroll-driven animation
                  <h2
                    style={{
                      fontSize: "84px",
                      fontWeight: 900,
                      textTransform: "uppercase",
                      margin: 0,
                      lineHeight: 0.85,
                      letterSpacing: "-2px",
                      color: isActive ? THEME.colors.primary : "transparent",
                      WebkitTextStroke: isActive ? "none" : "2px #a1a1aa", // Zinc 400 stroke
                      opacity: isActive ? 1 : Math.min(showProgress + 0.3, 1),
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "flex-start",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.4em",
                        marginRight: "24px",
                        marginTop: "12px",
                        color: isActive ? THEME.colors.accent : "transparent", // Gold Number
                        WebkitTextStroke: isActive ? "none" : "1px #a1a1aa",
                      }}
                    >
                      {item.id}.
                    </span>
                    {item.title}
                  </h2>
                )}
              </div>
            );
          })}
        </div>

        {/* RIGHT COLUMN: DETAIL CONTENT (Desktop Only Sticky) */}
        {isDesktop && (
          <div
            style={{
              flex: "1",
              paddingTop: "24px",
              position: "sticky",
              top: "150px", // Stick to top as user scrolls past
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: 800,
                    color: THEME.colors.accent, // Gold Heading
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    marginBottom: "24px",
                    display: "block",
                  }}
                >
                  {PILLARS_DATA[activeIndex].heading}
                </h4>

                <p
                  style={{
                    fontSize: "18px",
                    fontWeight: 600,
                    lineHeight: 1.7,
                    color: "#52525b", // Zinc 600
                    marginBottom: "48px",
                    maxWidth: "480px",
                  }}
                >
                  {PILLARS_DATA[activeIndex].description}
                </p>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    backgroundColor: THEME.colors.primary,
                    color: "white",
                    border: "none",
                    padding: "16px 32px",
                    fontWeight: "bold",
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    cursor: "pointer",
                    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                  }}
                >
                  Explore More
                  <ArrowRight size={16} />
                </motion.button>
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>
    </section>
  );
};

export default PillarsSection;
